import React from "react";
import { BookOpen, HelpCircle, RotateCcw } from "lucide-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import UserSettingsManager, { type UserSettingsCourse } from "@/components/student/UserSettingsManager";
import ProfileNameCard from "@/components/student/ProfileNameCard";
import SettingsBackButton from "@/components/student/SettingsBackButton";
import { startOnboardingTour } from "@/components/student/OnboardingTour";
import {
  getCourseUserSettingsApiV1CoursesCourseIdUserSettingsGet,
  listPublishedCoursesApiV1CoursesGet,
} from "@/src/api/sdk.gen";
import type { LessonLanguage } from "@/lib/lesson-language";

export default function SettingsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get("courseId");

  // Single course query when courseId is provided in URL
  const {
    data: singleCourseSettings,
    isLoading: isSingleLoading,
    error: singleError,
  } = useQuery({
    queryKey: ["course-user-settings", courseId],
    queryFn: async () => {
      const { data, error } = await getCourseUserSettingsApiV1CoursesCourseIdUserSettingsGet({
        path: { course_id: courseId! },
      });
      if (error) throw new Error("Failed to fetch course settings");
      return data;
    },
    enabled: Boolean(courseId),
  });

  // Multiple courses query when no courseId is provided
  const { data: publishedCoursesData, isLoading: isCoursesLoading } = useQuery({
    queryKey: ["student-courses-list", 1, 50],
    queryFn: async () => {
      const { data } = await listPublishedCoursesApiV1CoursesGet({
        query: { page: 1, limit: 50 },
      });
      return Array.isArray(data) ? data : (data as any)?.courses || [];
    },
    enabled: !courseId,
  });

  const { data: allCoursesSettings, isLoading: isAllLoading } = useQuery({
    queryKey: ["all-courses-user-settings", publishedCoursesData],
    queryFn: async () => {
      if (!publishedCoursesData || publishedCoursesData.length === 0) return [];
      const results = await Promise.all(
        publishedCoursesData.map(async (course: any, idx: number) => {
          const idOrUuid = course.uuid || String(course.id);
          try {
            const { data } = await getCourseUserSettingsApiV1CoursesCourseIdUserSettingsGet({
              path: { course_id: idOrUuid },
            });
            if (!data) return null;
            return {
              settingsId: idx + 1,
              courseId: course.id,
              courseUuid: course.uuid || String(course.id),
              courseTitle: data.course?.title || course.title,
              domainId: data.settings?.domainId ?? null,
              futureGoalId: data.settings?.futureGoalId ?? null,
              departmentId: data.settings?.departmentId ?? null,
              language: (data.settings?.language === "Arabic" ? "Arabic" : "English") as LessonLanguage,
              domains: data.domains || [],
              futureGoals: data.futureGoals || [],
              departments: data.departments || [],
            } as UserSettingsCourse;
          } catch {
            return null;
          }
        }),
      );
      return results.filter(Boolean) as UserSettingsCourse[];
    },
    enabled: !courseId && Boolean(publishedCoursesData && publishedCoursesData.length > 0),
  });

  const isLoading = courseId ? isSingleLoading : (isCoursesLoading || isAllLoading);

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ProfileNameCard />
        <div className="flex justify-center py-4">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (courseId && singleError) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ProfileNameCard />
        <div className="mb-6 rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 shadow-sm">
          <div className="flex items-start gap-3">
            <SettingsBackButton />
            <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
              <BookOpen className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold tracking-tight text-slate-900">Course Settings</h1>
              <p className="mt-1 text-sm text-slate-600">
                Review and update your domain, future goal, and lesson language for this course.
              </p>
            </div>
          </div>
        </div>
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center text-red-700">
          <p className="text-sm font-medium">Failed to load settings for this course. Please try again.</p>
        </div>
      </div>
    );
  }

  const courseTitle = courseId && singleCourseSettings?.course?.title
    ? singleCourseSettings.course.title
    : null;

  const courses: UserSettingsCourse[] = courseId
    ? singleCourseSettings
      ? [
          {
            settingsId: 1,
            courseId: singleCourseSettings.course?.id || courseId,
            courseUuid: singleCourseSettings.course?.id || courseId,
            courseTitle: singleCourseSettings.course?.title || "Course",
            domainId: singleCourseSettings.settings?.domainId ?? null,
            futureGoalId: singleCourseSettings.settings?.futureGoalId ?? null,
            departmentId: singleCourseSettings.settings?.departmentId ?? null,
            language: (singleCourseSettings.settings?.language === "Arabic" ? "Arabic" : "English") as LessonLanguage,
            domains: singleCourseSettings.domains || [],
            futureGoals: singleCourseSettings.futureGoals || [],
            departments: singleCourseSettings.departments || [],
          },
        ]
      : []
    : allCoursesSettings || [];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <ProfileNameCard />
      <div className="mb-6 rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 shadow-sm">
        <div className="flex items-start gap-3">
          <SettingsBackButton />
          <div className="mt-0.5 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
            <BookOpen className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              {courseTitle ? `${courseTitle} settings` : "Course Settings"}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Review and update your domain, future goal, and lesson language for {courseTitle ? "this course." : "each course."}
            </p>
            <p className="mt-0.5 text-sm text-slate-600">
              Language preference applies only to upcoming proactively generated lessons and will not change lessons already created.
            </p>
          </div>
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-slate-200 bg-white/95 px-5 py-4 shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 shrink-0">
            <HelpCircle className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-bold text-slate-900">Platform Onboarding Tour</h2>
            <p className="text-xs text-slate-600">
              Replay the interactive walkthrough of courses, personalized lessons, quizzes, and certificates.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => startOnboardingTour(navigate)}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold rounded-xl shadow-sm transition active:scale-95 shrink-0"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Replay Tour
        </button>
      </div>

      <UserSettingsManager courses={courses} />
    </div>
  );
}

